import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAuth, useLoading, useSnackbar } from "../../Context/Contexts";
import { ProgressResultViewModel } from "../../Types/ExportTypes";

type CardVoucherCancelDialogProps = {
  open: boolean;
  onClose: () => void;
};

const certificateTypes = [
  { value: "", labelKey: "" },
  { value: "0", labelKey: "common.giftCard" },
  { value: "1", labelKey: "common.downPayment" },
  { value: "2", labelKey: "common.settlementVoucher" },
  { value: "50", labelKey: "common.bonusCard" },
  { value: "51", labelKey: "common.bonusCheck" },
  { value: "56", labelKey: "common.bonusPoints" },
];

const CardVoucherCancelDialog: React.FC<CardVoucherCancelDialogProps> = ({ open, onClose }) => {
  const { authService } = useAuth();
  const { showSnackbar } = useSnackbar();
  const { showLoading, hideLoading } = useLoading();
  const { t } = useTranslation();

  const [type, setType] = useState<string>("");
  const [validUntil, setValidUntil] = useState<string>("");
  const [postingDate, setPostingDate] = useState<string>("");
  const [progressText, setProgressText] = useState<string>("");
  const [progressValue, setProgressValue] = useState<number>(0);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (!open) return;
    const today = new Date().toISOString().slice(0, 10);
    setType("");
    setValidUntil(today);
    setPostingDate(today);
    setProgressText("");
    setProgressValue(0);
    setProcessing(false);
  }, [open]);

  const formattedValidUntil = useMemo(
    () => (validUntil ? new Date(validUntil).toISOString().slice(0, 10) : ""),
    [validUntil]
  );

  const formattedPostingDate = useMemo(
    () => (postingDate ? new Date(postingDate).toISOString().slice(0, 10) : ""),
    [postingDate]
  );

  const pollStatus = async (progressId: number) => {
    const interval = 3000;
    let attempts = 200;

    while (attempts > 0) {
      try {
        const response = await authService.get<ProgressResultViewModel>(`/progressApi/GetProgress?progressId=${progressId}`);
        setProgressText(response.text ?? "");
        setProgressValue(response.progress ?? 0);

        if (response.error) {
          showSnackbar(response.text ?? t("dialog.fetchStatusError"), "error");
          break;
        }

        if (response.done) {
          showSnackbar(t("dialog.savedSuccessfully"), "success");
          break;
        }
      } catch (error: any) {
        showSnackbar(error.response?.data ?? error.message, "error");
        break;
      }

      attempts--;
      await new Promise((resolve) => setTimeout(resolve, interval));
    }
  };

  const startCancellation = async () => {
    setProcessing(true);
    showLoading();
    setProgressText("");
    setProgressValue(0);
    try {
      const payload = {
        type: type ? Number(type) : null,
        validUntil: formattedValidUntil ? new Date(formattedValidUntil).toISOString() : null,
        postingDate: formattedPostingDate ? new Date(formattedPostingDate).toISOString() : null,
      };

      const response = await authService.post<{ progressId: number }>("/cardVoucherApi/CancelBatch", payload);
      await pollStatus(response.progressId);
    } catch (error: any) {
      showSnackbar(error.response?.data ?? error.message, "error");
    } finally {
      hideLoading();
      setProcessing(false);
    }
  };

  const handleClose = () => {
    if (processing) return;
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>{t("common.cancelCertificates")}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3} sx={{ pt: 1 }}>
          <Typography>{t("common.cancelCertificatesDescription")}</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                select
                label={t("common.type")}
                value={type}
                onChange={(event) => setType(event.target.value)}
                fullWidth
                size="small"
              >
                {certificateTypes.map((option) => (
                  <MenuItem key={option.value || "empty"} value={option.value}>
                    {option.labelKey ? t(option.labelKey) : ""}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label={t("common.validUntil")}
                type="date"
                value={formattedValidUntil}
                onChange={(event) => setValidUntil(event.target.value)}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label={t("common.postingDate")}
                type="date"
                value={formattedPostingDate}
                onChange={(event) => setPostingDate(event.target.value)}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>

          <Box>
            <Typography variant="body2" gutterBottom>
              {progressText || t("common.progressAwaiting")}
            </Typography>
            <LinearProgress variant="determinate" value={progressValue} />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={processing}>
          {t("common.cancel")}
        </Button>
        <Button variant="contained" onClick={startCancellation} disabled={processing}>
          {t("common.startCancellation")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CardVoucherCancelDialog;
